import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import useUsers from 'contexts/basicData/useUsers';
import styled from 'styled-components';
import Input from 'components/inputs/Input';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import { ColumnSetting } from 'components/Table/utils';
import Table from 'components/Table';
import { usePaymentCardFindPaymentCards } from 'api/payment-card/payment-card';
import {
  ExternalReceiptProvider,
  PaymentCardModel,
  TranslationLookups,
} from 'api/model';
import {
  useChargingKeyGetChargingKeys,
  useChargingKeyImportChargingSessions,
  useChargingKeySyncChargingKeys,
} from 'api/charging-key/charging-key';
import useModalStack from 'contexts/modal/useModalStack';
import PaymentCardFormModal from './PaymentCardFormModal';
import Button from 'components/inputs/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BusinessUnitSelect from 'components/inputs/BusinessUnitSelect';
import {
  faBan,
  faChargingStation,
  faCheck,
  faInfoCircle,
  faKey,
  faPlus,
  faSpinner,
  faSync,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import PaymentCardMappingModal from './PaymentCardMappingModal';
import ChargingKeyModal from './ChargingKeyModal';
import ActivateChargingKeyModal from './ActivateChargingKeyModal';
import Checkbox from 'components/inputs/Checkbox';
import { useLocalStorage } from 'usehooks-ts';
import useTranslations from 'contexts/basicData/useTranslations';
import useMe from 'contexts/authentication/useMe';
import Roles from 'constants/Roles';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';

const isChargingKey = (card: PaymentCardModel) =>
  card.provider === ExternalReceiptProvider.FortumChargeDrive;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FullSpan = styled.div`
  display: flex;
  flex: 1;
  cursor: pointer;
  white-space: break-spaces;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 50px;
  padding: 10px;
  gap: 10px;
`;

const MyButton = styled(Button)`
  padding: 0 10px;
  height: 100%;
`;

const Right = styled.div`
  margin-left: auto;
  display: flex;
  gap: 10px;
  align-items: center;
  height: 100%;
`;

const PaymentCards = () => {
  const [paymentCards, setPaymentCards] = useState<PaymentCardModel[]>([]);
  const modalStack = useModalStack();
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<number[]>(
    []
  );
  const [search, setSearch] = useState<string>('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const users = useUsers();
  const [hideInactive, setHideInactive] = useLocalStorage<boolean>(
    'hide-inactive-payment-cards',
    true
  );

  const findPaymentCards = usePaymentCardFindPaymentCards();
  const { externalReceiptProviders } = useTranslations();
  const me = useMe();
  const canEdit = me?.roles.includes(Roles.CircleK);
  const queryClient = useQueryClient();
  const chargingKeys = useChargingKeyGetChargingKeys();
  const chargingKeysIntegrationEnabled =
    chargingKeys.data?.integrationEnabled ?? false;
  const syncChargingKeys = useChargingKeySyncChargingKeys();
  const importChargingSessions = useChargingKeyImportChargingSessions();

  const fetchPaymentCards = async () => {
    await findPaymentCards.mutateAsync({
      data: {
        search,
        businessUnitIds: selectedBusinessUnits,
      },
    });
  };

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchPaymentCards();
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedBusinessUnits]);

  useEffect(() => {
    if (findPaymentCards.data) {
      const filteredPaymentCards = findPaymentCards.data.filter(
        (paymentCard) => !hideInactive || (hideInactive && paymentCard.isActive)
      );
      setPaymentCards(filteredPaymentCards);
    }
  }, [findPaymentCards.data, hideInactive]);

  const columnSettings: ColumnSetting<PaymentCardModel, TranslationLookups>[] =
    useMemo(() => {
      const columns: ColumnSetting<PaymentCardModel, TranslationLookups>[] = [
        {
          head: 'Nr',
          render: (item, renderProps, focused, index) => (
            <FullSpan>{index + 1}</FullSpan>
          ),
          width: 10,
        },
        {
          head: (
            <span>
              Aktiv{' '}
              <FontAwesomeIcon
                icon={faInfoCircle}
                title="Inaktiva kort är kort som är exempelvis klippta eller spärrade och inte längre ska användas."
              />
            </span>
          ),
          render: (item) => (
            <FullSpan>
              {item.isActive ? (
                <span>
                  <FontAwesomeIcon icon={faCheck} color="green" />
                </span>
              ) : (
                <span>
                  <FontAwesomeIcon icon={faBan} color="gray" />
                </span>
              )}
            </FullSpan>
          ),
          width: 40,
          sortFunction: (a, b) => (a.isActive ? 1 : 0) - (b.isActive ? 1 : 0),
        },
        {
          head: 'Kortnummer',
          // Laddnycklar visas med numret som står på taggen; UID:t finns i tooltip och i modalen
          render: (item) =>
            isChargingKey(item) ? (
              <FullSpan title={`UID ${item.number}`}>
                {item.visualNumber ?? item.number}
              </FullSpan>
            ) : (
              <FullSpan>{item.number}</FullSpan>
            ),
          width: 120,
          sortFunction: (a, b) =>
            (a.visualNumber ?? a.number).localeCompare(
              b.visualNumber ?? b.number
            ),
        },
        {
          head: 'Leverantör',
          render: (item) => (
            <FullSpan>{externalReceiptProviders[item.provider] ?? ''}</FullSpan>
          ),
          width: 100,
          sortFunction: (a, b) => a.provider - b.provider,
        },
        {
          head: 'Namn',
          render: (item) => (
            <FullSpan>
              {item.cardHolderID ? users[item.cardHolderID]?.name : ''}
            </FullSpan>
          ),
          width: 150,
          sortFunction: (a, b) => {
            const nameA = users[a.cardHolderID ?? -1]?.name ?? '';
            const nameB = users[b.cardHolderID ?? -1]?.name ?? '';
            return nameA.localeCompare(nameB);
          },
        },
        {
          head: 'ÅÅMM',
          render: (item) => <FullSpan>{item.expiration}</FullSpan>,
          width: 160,
          sortFunction: (a, b) => a.expiration.localeCompare(b.expiration),
        },
      ];
      return columns;
    }, [users, externalReceiptProviders]);

  const refreshAll = async () => {
    await queryClient.invalidateQueries(chargingKeys.queryKey);
    await fetchPaymentCards();
  };

  const handleRowClick = (item: PaymentCardModel) => {
    modalStack.push(
      isChargingKey(item) ? (
        <ChargingKeyModal paymentCardId={item.id} onUpdated={refreshAll} />
      ) : (
        <PaymentCardFormModal id={item.id} onUpdated={fetchPaymentCards} />
      )
    );
  };

  const handleSyncChargingKeys = async () => {
    try {
      const result = await syncChargingKeys.mutateAsync();
      const missing =
        result.missingAtFortum.length > 0
          ? ` Finns inte längre hos Fortum: ${result.missingAtFortum.join(', ')}.`
          : '';
      toast.success(
        `${result.totalAtFortum} nycklar hos Fortum: ${result.created} nya, ${result.updated} uppdaterade.${missing}`,
        { duration: 8000 }
      );
      await refreshAll();
    } catch {
      // Felet visas som toast av den globala felhanteringen
    }
  };

  // Samma import som timern i Azure Functions gör var 10:e minut
  const handleImportChargingSessions = async () => {
    try {
      const result = await importChargingSessions.mutateAsync();
      const unknownKeys =
        result.unknownKeys > 0
          ? ` ${result.unknownKeys} okända nycklar lades till — kör Synka laddnycklar.`
          : '';
      toast.success(
        result.imported === 0
          ? `Inga nya laddsessioner (${result.fetched} hos Fortum för ${result.months.join(' och ')}).`
          : `${result.imported} nya laddsessioner: ${result.expensesCreated} blev utlägg, ${result.unmatched} väntar på manuell koppling${
              result.ignored > 0
                ? `, ${result.ignored} under 0,5 kWh räknades inte`
                : ''
            }.${unknownKeys}`,
        { duration: 8000 }
      );
      await refreshAll();
    } catch {
      // Felet visas som toast av den globala felhanteringen
    }
  };
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Wrapper>
        <Actions>
          <Checkbox
            checked={hideInactive}
            onChange={(e) => setHideInactive(e.currentTarget.checked)}
          >
            Dölj inaktiva
          </Checkbox>
          <MyButton
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => {
              modalStack.push(
                <PaymentCardFormModal onUpdated={fetchPaymentCards} />
              );
            }}
          >
            Lägg till
          </MyButton>
          <Input
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtrera på namn, kortnummer eller utgångsdatum"
            value={search}
            width={230}
          />
          <BusinessUnitSelect
            onChange={(units) => {
              setSelectedBusinessUnits(Array.from(units));
            }}
            value={new Set(selectedBusinessUnits)}
          />
          {findPaymentCards.isLoading && (
            <FontAwesomeIcon icon={faSpinner} spin />
          )}
          <Right>
            {canEdit && chargingKeysIntegrationEnabled && (
              <>
                <Button
                  icon={
                    <FontAwesomeIcon
                      icon={faSync}
                      spin={syncChargingKeys.isLoading}
                    />
                  }
                  disabled={syncChargingKeys.isLoading}
                  onClick={handleSyncChargingKeys}
                >
                  Synka laddnycklar
                </Button>
                <Button
                  icon={
                    <FontAwesomeIcon
                      icon={
                        importChargingSessions.isLoading
                          ? faSpinner
                          : faChargingStation
                      }
                      spin={importChargingSessions.isLoading}
                    />
                  }
                  disabled={importChargingSessions.isLoading}
                  onClick={handleImportChargingSessions}
                >
                  Hämta laddsessioner
                </Button>
                <Button
                  icon={<FontAwesomeIcon icon={faKey} />}
                  onClick={() => {
                    modalStack.push(
                      <ActivateChargingKeyModal onUpdated={refreshAll} />
                    );
                  }}
                >
                  Aktivera laddnyckel
                </Button>
              </>
            )}
            <Button
              icon={<FontAwesomeIcon icon={faUpload} />}
              onClick={() => {
                modalStack.push(
                  <PaymentCardMappingModal onUpdated={fetchPaymentCards} />
                );
              }}
            >
              Mappa kort
            </Button>
          </Right>
        </Actions>
        {paymentCards !== undefined && paymentCards.length >= 0 ? (
          <Table
            useColumnWidthAsFlexAmount
            columnSettings={columnSettings}
            onRowClick={handleRowClick}
            rows={paymentCards}
          />
        ) : (
          <LoadingSpinner />
        )}
      </Wrapper>
    </Suspense>
  );
};

export default PaymentCards;
